import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import SpinnerButton from "@/components/SpinnerButton";
import { adminApi } from "@/lib/api";
import { errText } from "@/lib/ranges";
import { awayByDay, dayLabel as longDay, monthBounds, normaliseLeave, OnLeaveList, spanLabel } from "@/lib/leave";

function ym(year, month) { return `${year}-${String(month + 1).padStart(2, "0")}`; }
function dayLabel(idx) { return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][idx]; }
function calendarGrid(year, month) {
  const first = new Date(year, month, 1);
  const leading = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < leading; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const COLORS = ["#5C5C5C", "#7a6b56", "#4f6e5d", "#7a4c4c", "#4d5e7a", "#6f4d7a"];

export default function AdminDuty() {
  const t = new Date();
  const [view, setView] = useState({ year: t.getFullYear(), month: t.getMonth() });
  const [assignments, setAssignments] = useState([]);
  const [offDays, setOffDays] = useState([]);
  const [team, setTeam] = useState([]);
  const [openDay, setOpenDay] = useState(null);
  const [autofillOpen, setAutofillOpen] = useState(false);
  // Pass 60 — planned leave and the clashes it causes.
  const [leaveRows, setLeaveRows] = useState([]);
  const [clashes, setClashes] = useState([]);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [notice, setNotice] = useState("");

  const load = async () => {
    const start = `${view.year}-${String(view.month + 1).padStart(2, "0")}-01`;
    const lastDay = new Date(view.year, view.month + 1, 0).getDate();
    const end = `${view.year}-${String(view.month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    const [a, o, m, lv] = await Promise.all([
      adminApi.duty({ from: start, to: end }),
      adminApi.offdays(),
      adminApi.team(),
      adminApi.leave().catch(() => ({ data: { leave: [], clashes: [] } })),
    ]);
    setLeaveRows(lv.data.leave || []);
    setClashes(lv.data.clashes || []);
    setAssignments(a.data.assignments);
    setOffDays(o.data.off_days);
    // Accept "TRUE", "true", "1", "yes" — be liberal in what we read from the Sheet.
    // Pass 43 — the KL duty calendar is KL-only. JB members are
    // filtered out client-side so they never appear in the assign /
    // auto-fill dropdowns; server-side `admin_duty_assign` /
    // `admin_duty_autofill` reject them anyway, but filtering here
    // keeps the UI honest.
    setTeam(m.data.members.filter((x) => {
      const active = String(x.Active ?? "TRUE").trim().toUpperCase();
      const isActive = active === "" || active === "TRUE" || active === "1" || active === "YES";
      const isKl = (String(x.Store || "").trim().toUpperCase() || "KL") === "KL";
      return isActive && isKl;
    }));
  };

  useEffect(() => { load(); }, [view]);

  const assignMap = useMemo(() => {
    const m = {};
    for (const a of assignments) m[a.Date] = a;
    return m;
  }, [assignments]);

  const offMap = useMemo(() => {
    const m = {};
    for (const o of offDays) m[o.Date] = o.Reason;
    return m;
  }, [offDays]);

  const leave = useMemo(() => normaliseLeave(leaveRows), [leaveRows]);
  const awayMap = useMemo(() => {
    const { from, to } = monthBounds(view.year, view.month);
    return awayByDay(leave, from, to);
  }, [leave, view]);
  const clashDays = useMemo(() => new Set(clashes.map((c) => c.date)), [clashes]);

  const personColor = useMemo(() => {
    const m = {};
    team.forEach((t, idx) => { m[t.Name] = COLORS[idx % COLORS.length]; });
    return m;
  }, [team]);

  return (
    <AdminLayout>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Schedule</p>
      <h1 style={{ fontSize: 28, margin: "0 0 28px 0" }}>Duty schedule</h1>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <h3 style={{ margin: 0, order: 0 }} data-testid="duty-month-label">{new Date(view.year, view.month, 1).toLocaleString("en-US", { month: "long", year: "numeric" })}</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }} onClick={() => {
            const m = view.month - 1;
            if (m < 0) setView({ year: view.year - 1, month: 11 });
            else setView({ year: view.year, month: m });
          }} data-testid="duty-prev-btn">{'\u2190'} Prev</button>
          <button className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }} onClick={() => setAutofillOpen(true)} data-testid="duty-autofill-btn">Auto-fill</button>
          <button className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }} onClick={() => setLeaveOpen(true)} data-testid="duty-leave-btn">Leave</button>
          <button className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }} onClick={() => {
            const m = view.month + 1;
            if (m > 11) setView({ year: view.year + 1, month: 0 });
            else setView({ year: view.year, month: m });
          }} data-testid="duty-next-btn">Next {'\u2192'}</button>
        </div>
      </div>

      {notice && (
        <div style={{ padding: "10px 14px", border: "1px solid #e8d9c5", background: "#fcf8f4", color: "#8a6d3b", fontSize: 13, marginBottom: 14 }} data-testid="duty-notice">
          {notice} <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "2px 10px", marginLeft: 8 }} onClick={() => setNotice("")}>OK</button>
        </div>
      )}
      <ClashBanner clashes={clashes} onPick={(iso) => {
        const [y, mo] = iso.split("-").map(Number);
        setView({ year: y, month: mo - 1 });
        setOpenDay(iso);
      }} />

      <div className="scw-duty-header" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 6 }}>
        {[0,1,2,3,4,5,6].map((i) => (
          <div key={i} style={{ textAlign: "center", fontSize: 11, color: "var(--scw-muted)", letterSpacing: "0.16em", padding: "8px 0" }}>{dayLabel(i)}</div>
        ))}
      </div>
      <div className="scw-duty-grid" style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
        {calendarGrid(view.year, view.month).map((d, idx) => {
          if (d === null) return <div key={idx} className="scw-duty-day-empty" />;
          const ds = `${view.year}-${String(view.month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const assignment = assignMap[ds];
          const isOff = ds in offMap;
          const wd = new Date(view.year, view.month, d).getDay();
          const isSunday = wd === 0;
          const bg = isSunday || isOff ? "#f4f4f4" : "#FFFFFF";
          // Mon-Sun JS-getDay mapping: 0=Sun,1=Mon...6=Sat. Our array is
          // Mon-first, so re-index: (getDay()+6)%7.
          const weekdayShort = dayLabel((wd + 6) % 7);
          return (
            <button key={idx} type="button" onClick={() => setOpenDay(ds)}
                    className="scw-duty-day"
                    style={{ aspectRatio: "1 / 1.1", background: bg, border: clashDays.has(ds) ? "2px solid #a13b3b" : "1px solid var(--scw-line)", padding: 8, cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", justifyContent: "flex-start" }}
                    data-testid={`duty-day-${ds}`}>
              <div>
                <div className="scw-duty-day-num" style={{ fontSize: 13, fontWeight: 500 }}>{d}</div>
                <div className="scw-duty-day-meta">{weekdayShort}</div>
              </div>
              {!isSunday && <OnLeaveList names={awayMap[ds]} testId={`duty-leave-${ds}`} />}
              <div className="scw-duty-day-right" style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: "auto" }}>
                {isOff && (
                  <div style={{ fontSize: 10, color: "var(--scw-muted)", fontStyle: "italic" }}>{offMap[ds] || "Closed"}</div>
                )}
                {isSunday && !isOff && (
                  <div style={{ fontSize: 10, color: "var(--scw-muted)", fontStyle: "italic" }}>Closed</div>
                )}
                {/* Sundays are never staffed, so we don't surface an assignment
                    pill for them — but on a public holiday (Off_Days entry on
                    a non-Sunday) the admin may still want to forward leads
                    to a specialist, so show the pill alongside the holiday
                    label. */}
                {!isSunday && assignment && (
                  <div style={{ fontSize: 11, color: "#FFFFFF", background: personColor[assignment.Person_Name] || "#5C5C5C", padding: "3px 6px", letterSpacing: "0.06em" }}>{assignment.Person_Name}</div>
                )}
                {!isSunday && !isOff && !assignment && (
                  <div style={{ fontSize: 10, color: "var(--scw-muted)" }}>Unassigned</div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {openDay && (
        <AssignModal date={openDay} team={team} current={assignMap[openDay]}
                     away={awayByDay(leave, openDay, openDay)[openDay] || []}
                     clashes={clashes.filter((c) => c.date === openDay)}
                     onClose={() => setOpenDay(null)}
                     onSave={async (name, notes) => {
                       await adminApi.dutyAssign({ date: openDay, person_name: name, notes });
                       setOpenDay(null); load();
                     }}
                     onDelete={async () => { await adminApi.dutyDelete(openDay); setOpenDay(null); load(); }}
        />
      )}
      {autofillOpen && (
        <AutofillModal team={team} onClose={() => setAutofillOpen(false)}
                       onSave={async (body) => {
                         const r = await adminApi.dutyAutofill(body);
                         setAutofillOpen(false);
                         const skipped = r.data.unassigned_all_on_leave || [];
                         if (skipped.length) setNotice(`Left unassigned because everyone in the rotation is on leave: ${skipped.map(longDay).join(", ")}.`);
                         load();
                       }} />
      )}
      {leaveOpen && (
        <LeaveModal team={team} leave={leave} onClose={() => setLeaveOpen(false)} onChanged={load} />
      )}
    </AdminLayout>
  );
}

function ClashBanner({ clashes, onPick }) {
  const [open, setOpen] = useState(false);
  if (!clashes.length) return null;
  const shown = open ? clashes : clashes.slice(0, 5);
  return (
    <div style={{ border: "1px solid #e3c4c4", background: "#fbf3f3", padding: "12px 16px", marginBottom: 16 }} data-testid="duty-clash-banner">
      <p style={{ margin: "0 0 8px 0", fontSize: 13, color: "#a13b3b", fontWeight: 500 }}>
        {clashes.length} leave clash{clashes.length === 1 ? "" : "es"} to fix
      </p>
      {shown.map((c) => (
        <button key={`${c.date}-${c.kind}`} type="button" onClick={() => onPick(c.date)}
          style={{ display: "block", background: "none", border: "none", padding: "3px 0", cursor: "pointer", textAlign: "left", fontSize: 13, color: "#3D3D3D", fontFamily: "inherit" }}
          data-testid={`duty-clash-${c.date}-${c.kind}`}>
          <span style={{ textDecoration: "underline" }}>{longDay(c.date)}</span>{": "}{c.message}
        </button>
      ))}
      {clashes.length > 5 && (
        <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "2px 10px", marginTop: 6 }} onClick={() => setOpen(!open)}>
          {open ? "Show fewer" : `Show all ${clashes.length}`}
        </button>
      )}
    </div>
  );
}

function LeaveModal({ team, leave, onClose, onChanged }) {
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const [memberId, setMemberId] = useState(team[0]?.ID || "");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const upcoming = leave.filter((l) => l.end >= todayIso);

  const save = async () => {
    setErr(""); setMsg("");
    if (!memberId || !start || !end) { setErr("Choose a person and both dates."); return; }
    setBusy(true);
    try {
      const r = await adminApi.leaveCreate({ member_id: memberId, start_date: start, end_date: end || start });
      const found = r.data.clashes || [];
      setMsg(found.length ? `Saved. ${found.length} clash${found.length === 1 ? "" : "es"} to fix: ${found.map((c) => `${longDay(c.date)}, ${c.message}`).join(" ")}` : "Saved. No clashes.");
      setStart(""); setEnd("");
      onChanged();
    } catch (e) {
      setErr(errText(e, "Could not save."));
    } finally { setBusy(false); }
  };

  const cancel = async (id) => {
    setErr(""); setMsg("");
    try { await adminApi.leaveCancel(id); onChanged(); } catch (e) { setErr(errText(e, "Could not cancel.")); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={onClose}>
      <div style={{ background: "#FFFFFF", padding: 28, width: "min(560px, 100%)", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="duty-leave-modal">
        <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Planned leave</p>
        <h3 style={{ fontSize: 22, margin: "0 0 6px 0" }}>Leave on the duty calendar</h3>
        <p style={{ fontSize: 12, color: "var(--scw-muted)", margin: "0 0 18px 0", lineHeight: 1.6 }}>
          For planning only. Approve leave in AltHR first. The calendar shows "On leave" and nothing else.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>
            <label className="scw-label">Person</label>
            <select className="scw-select" value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="duty-leave-person">
              {team.map((t) => <option key={t.ID} value={t.ID}>{t.Name}</option>)}
            </select>
          </div>
          <div>
            <label className="scw-label">First day</label>
            <input type="date" className="scw-input" value={start} onChange={(e) => { setStart(e.target.value); if (!end || end < e.target.value) setEnd(e.target.value); }} data-testid="duty-leave-start" />
          </div>
          <div>
            <label className="scw-label">Last day</label>
            <input type="date" className="scw-input" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} data-testid="duty-leave-end" />
          </div>
        </div>
        {err && <p style={{ color: "#a13b3b", fontSize: 13, margin: "12px 0 0 0" }} data-testid="duty-leave-err">{err}</p>}
        {msg && <p style={{ color: msg.includes("clash") ? "#a13b3b" : "#3D3D3D", fontSize: 13, margin: "12px 0 0 0" }} data-testid="duty-leave-msg">{msg}</p>}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <SpinnerButton loading={busy} type="button" onClick={save} dataTestId="duty-leave-save">Add leave</SpinnerButton>
        </div>

        <p className="scw-eyebrow" style={{ margin: "24px 0 8px 0" }}>Upcoming leave</p>
        {upcoming.length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--scw-muted)" }}>None planned.</p>
        ) : upcoming.map((l) => (
          <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--scw-line)" }} data-testid={`duty-leave-row-${l.id}`}>
            <span style={{ flex: "0 0 110px", fontSize: 13 }}>{l.name}</span>
            <span style={{ flex: 1, fontSize: 13, color: "#5C5C5C" }}>{spanLabel(l.start, l.end)}</span>
            <button type="button" className="scw-btn scw-btn-ghost" style={{ padding: "4px 10px" }} onClick={() => cancel(l.id)} data-testid={`duty-leave-cancel-${l.id}`}>Cancel</button>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <button className="scw-btn scw-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ date, team, current, away, clashes, onClose, onSave, onDelete }) {
  const [name, setName] = useState(current?.Person_Name || (team[0]?.Name || ""));
  const [notes, setNotes] = useState(current?.Notes || "");
  const [loading, setLoading] = useState(false);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={onClose}>
      <div style={{ background: "#FFFFFF", padding: 28, width: "min(440px, 100%)" }} onClick={(e) => e.stopPropagation()} data-testid="duty-assign-modal">
        <p className="scw-eyebrow" style={{ marginBottom: 8 }}>{date}</p>
        <h3 style={{ fontSize: 22, margin: "0 0 18px 0" }}>Assign duty</h3>
        <label className="scw-label">Person</label>
        <select className="scw-select" value={name} onChange={(e) => setName(e.target.value)} data-testid="duty-assign-person">
          {team.map((t) => <option key={t.ID} value={t.Name}>{t.Name}</option>)}
        </select>
        {away.includes(name) && (
          <p style={{ color: "#a13b3b", fontSize: 13, margin: "10px 0 0 0" }} data-testid="duty-assign-on-leave">
            {name} is on leave on this day. Choose someone else, or save anyway.
          </p>
        )}
        {away.length > 0 && (
          <p style={{ color: "#8a6d3b", fontSize: 12, margin: "8px 0 0 0" }}>On leave: {away.join(", ")}</p>
        )}
        {clashes.filter((c) => c.kind === "short_cover").map((c) => (
          <p key="cover" style={{ color: "#a13b3b", fontSize: 12, margin: "6px 0 0 0" }}>{c.message}</p>
        ))}
        <div style={{ marginTop: 16 }}>
          <label className="scw-label">Notes</label>
          <input className="scw-input" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="duty-assign-notes" />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "space-between" }}>
          {current ? <button className="scw-btn scw-btn-ghost" onClick={async () => { setLoading(true); await onDelete(); setLoading(false); }} data-testid="duty-assign-clear">Clear</button> : <span />}
          <div style={{ display: "flex", gap: 10 }}>
            <button className="scw-btn scw-btn-ghost" onClick={onClose}>Cancel</button>
            <SpinnerButton loading={loading} type="button" onClick={async () => { setLoading(true); await onSave(name, notes); setLoading(false); }} dataTestId="duty-assign-save">Save</SpinnerButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function AutofillModal({ team, onClose, onSave }) {
  const [startDate, setStartDate] = useState("");
  const [days, setDays] = useState(14);
  const [rotation, setRotation] = useState(() => team.map((t) => t.Name));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // If team loads (or changes) after this modal mounts, seed the rotation
  // with the current team list once — avoids "empty rotation" race when the
  // Sheets fetch is slow or fails on the first paint.
  useEffect(() => {
    if (rotation.length === 0 && team.length > 0) {
      setRotation(team.map((t) => t.Name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team]);

  const move = (idx, dir) => {
    const arr = [...rotation];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setRotation(arr);
  };
  const remove = (idx) => setRotation(rotation.filter((_, i) => i !== idx));
  const add = (name) => { if (!rotation.includes(name)) setRotation([...rotation, name]); };

  const save = async () => {
    setErr("");
    if (!startDate || !rotation.length || days < 1) { setErr("Fill in all fields."); return; }
    setLoading(true);
    try { await onSave({ start_date: startDate, rotation_names: rotation, days: Number(days) }); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed."); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={onClose}>
      <div style={{ background: "#FFFFFF", padding: 28, width: "min(520px, 100%)" }} onClick={(e) => e.stopPropagation()} data-testid="duty-autofill-modal">
        <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Auto-fill rotation</p>
        <h3 style={{ fontSize: 22, margin: "0 0 18px 0" }}>Generate duty schedule</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label className="scw-label">Start date</label>
            <input type="date" className="scw-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="duty-autofill-start" />
          </div>
          <div>
            <label className="scw-label">Days</label>
            <input type="number" min={1} max={365} className="scw-input" value={days} onChange={(e) => setDays(e.target.value)} data-testid="duty-autofill-days" />
          </div>
        </div>
        <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Rotation order</p>
        <div style={{ border: "1px solid var(--scw-line)", padding: 8, marginBottom: 12 }}>
          {rotation.length === 0 ? (
            <div style={{ padding: "12px 4px", color: "var(--scw-muted)", fontSize: 13, fontStyle: "italic" }} data-testid="duty-autofill-empty">
              {team.length === 0
                ? "No active team members found. Add members in Team admin first."
                : "Rotation is empty. Use the dropdown below to add members."}
            </div>
          ) : rotation.map((n, idx) => (
            <div key={n} style={{ display: "flex", alignItems: "center", padding: "6px 0", borderBottom: idx < rotation.length - 1 ? "1px solid var(--scw-line)" : "none" }}>
              <span style={{ flex: 1 }}>{idx + 1}. {n}</span>
              <button className="scw-btn scw-btn-ghost" style={{ padding: "4px 10px", marginRight: 6 }} onClick={() => move(idx, -1)}>{'\u2191'}</button>
              <button className="scw-btn scw-btn-ghost" style={{ padding: "4px 10px", marginRight: 6 }} onClick={() => move(idx, 1)}>{'\u2193'}</button>
              <button className="scw-btn scw-btn-ghost" style={{ padding: "4px 10px" }} onClick={() => remove(idx)}>Remove</button>
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label className="scw-label">Add member</label>
          <select className="scw-select" defaultValue="" onChange={(e) => { if (e.target.value) { add(e.target.value); e.target.value = ""; } }} data-testid="duty-autofill-add-select">
            <option value="">{team.filter((m) => !rotation.includes(m.Name)).length === 0 ? "All members already added" : "Select to add\u2026"}</option>
            {team.filter((m) => !rotation.includes(m.Name)).map((m) => <option key={m.ID} value={m.Name}>{m.Name}</option>)}
          </select>
        </div>
        {err && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="scw-btn scw-btn-ghost" onClick={onClose}>Cancel</button>
          <SpinnerButton loading={loading} type="button" onClick={save} dataTestId="duty-autofill-save">Generate</SpinnerButton>
        </div>
      </div>
    </div>
  );
}
